import ComponentBase from "@components/ComponentBase";
import { ComponentElem } from "@components/interfaces";
import PlanUnitCount from "@components/ui/PlanUnitCount";
import { Emitter } from "strict-event-emitter";
import { Product } from "@components/product/ProductTypes";
import PlanTimeSelect from "./PlanTimeSelect";
import Tools from "@ts/Tools";

type MyState = "hide" | "idle" | "selected" | "disabled";

export default class PlanOption extends ComponentBase {
  static generate(planData: Product.PlanData, timeDataIdList: string[], dateId: string) {
    return new PlanOption(this.$sample.clone()[0] as ComponentElem, planData, timeDataIdList, dateId);
  }

  data: Product.PlanData;
  timeDataIdList: string[];
  dateId: string;
  selectedTime: Product.TimeData;
  timeSelect: PlanTimeSelect;
  planUnitCountList: PlanUnitCount[] = [];
  passengerMax: number;
  passengerTotalCount: number = 0;
  totalCount: number;
  isMoreThanFull: boolean = false;
  isAllFull: boolean = false;
  state: MyState = "disabled";
  emitter: Emitter<{ "select": [isSelected: boolean], "time-select": [], "count-change": [] }> = new Emitter();

  constructor(elem: ComponentElem, data: Product.PlanData, timeDataIdList: string[], dateId: string) {
    super(elem);
    this._toggleCountsInput(false);
    this.dateId = dateId;
    this.applyData(data);
    this._setTimeSelect(timeDataIdList);
    this.passengerMax = parseInt(data.passenger_max);
    let $counts = this.$root.find(".counts");
    $counts.empty();

    data.unit_data.forEach((unitData: Product.UnitData) => {
      let planUnitCount: PlanUnitCount = PlanUnitCount.generate(unitData);
      this.planUnitCountList.push(planUnitCount);
      planUnitCount.setCount(0);
      $counts.append(planUnitCount.$root);
      planUnitCount.emitter.addListener("click", (type) => {
        if (unitData.unit_max === undefined) {
          let unitCount = unitData.count;
          let actionType = type === "add" ? 1 : -1;
          let newCount = unitCount + actionType;
          if (newCount < 0) {
            return;
          }
          if (unitData.is_passenger === true && this.passengerTotalCount + 1 > this.passengerMax) {
            return;
          }
          planUnitCount.setCount(type === "add" ? planUnitCount.getCount() + 1 : planUnitCount.getCount() - 1);
          this.updateCount();
        } else {
          let unitCount = unitData.count;
          let unitMax = unitData.unit_max;
          let actionType = type === "add" ? 1 : -1;
          let newCount = unitCount + actionType;
          if (newCount < 0) {
            return;
          }
          if (unitData.is_passenger === true && this.passengerTotalCount + 1 > this.passengerMax) {
            return;
          }
          if (unitCount > unitMax)
            return;
          planUnitCount.setCount(type === "add" ? planUnitCount.getCount() + 1 : planUnitCount.getCount() - 1);
          this.updateCount();
        }
      });
    });

    this.$root.find(".btn-select").on("click", (event: Event) => {
      event.preventDefault();
      this.emitter.emit("select", true);
    });

    this.$root.find(".btn-cancel").on("click", (event: Event) => {
      event.preventDefault();
      this.emitter.emit("select", false);
    });
    this.setState("disabled");
    this.updateCount();
  }

  reset() {
    this.timeSelect.reset();
    this.selectedTime = null;
    this._toggleCountsInput(false);
    this.planUnitCountList.forEach((planUnitCount) => {
      planUnitCount.setCount(0);
    });
    this.updateCount();
  }

  private _toggleCountsInput(b: boolean) {
    if (b) {
      this.$root.find(".title-for-count").css("display", "");
      this.$root.find(".counts").css("display", "");
    } else {
      this.$root.find(".title-for-count").css("display", "none");
      this.$root.find(".counts").css("display", "none");
    }
  }

  applyCountData(countData: { [key: string]: number }) {
    this.planUnitCountList.forEach((planUnitCount) => {
      let cartId = planUnitCount.data.id;
      if (countData[cartId] !== undefined)
        planUnitCount.setCount(countData[cartId]);
    });

    this.updateCount();
  }

  setUnitCountValue(unitId: string, count: number) {
    this.planUnitCountList.every((planUnitCount) => {
      if (planUnitCount.data.id === unitId) {
        planUnitCount.setCount(count);
        return false;
      }
      return true;
    });
    this.updateCount();
  }

  getData() {
    return this.data;
  }

  getName() {
    return this.data.name;
  }

  getSelectedTime() {
    return this.selectedTime;
  }

  getSelectedUnits() {
    let array: Product.SelectedUnitList = [];
    this.planUnitCountList.forEach((planUnitCount: PlanUnitCount) => {
      array.push({
        data: planUnitCount.getData(),
        count: planUnitCount.getCount()
      });
    });
    return array;
  }

  _setTimeSelect(timeDataIdList: string[]) {
    let timeListElem: ComponentElem = this.$root.find('[component="plan-time-select"]')[0] as ComponentElem,
      timeList: Product.TimeData[] = [];

    timeDataIdList.forEach((id: string) => {
      if (!this.data.time_dic[id])
        throw new Error(`TimeData id: [${id}] in DateData[${this.dateId}].available_time_list is not exist in PlanData[${this.data.id}]`);
      else
        timeList.push(this.data.time_dic[id]);
    });

    this.timeSelect = new PlanTimeSelect(timeListElem, timeList);
    this.timeSelect.emitter.addListener("change", (timeData) => {
      this._toggleCountsInput(true);
      this.selectedTime = timeData;
      this.emitter.emit("time-select");
    });
  }

  setState(state: MyState) {
    this.state = state;
    this.$root.attr("state", state);
  }

  applyData(data: Product.PlanData) {
    this.data = data;
    this.$root.find(".my-title > .text").text(data.name);
    if (data.description) {
      this.$root.find(".my-description").html(data.description);
    }
    else {
      this.$root.find(".my-description").css("display", "none");
    }
    let $myPrice = this.$root.find(".my-price");
    $myPrice.find(".unit").text(data.unit_text);

    if (data.promote_price) {
      this.$root.attr("data-with-promote", "true");
      Tools.formatNumber(data.unit_data[0].price);
      $myPrice.find(".normal-for-promote > .price").text(Tools.formatNumber(data.price));
      $myPrice.find(".promote > .price").text(Tools.formatNumber(data.promote_price));
    } else {
      this.$root.attr("data-with-promote", "false");
      $myPrice.find(".normal > .price").text(Tools.formatNumber(data.price));
    }
  }

  // stupid loop that run every fucking time updateCount fired
  updatePassengerCount() {
    let total = 0;
    let passengerTotal = 0;
    this.planUnitCountList.forEach((planUnitCount) => {
      if (planUnitCount.data.is_passenger) {
        passengerTotal += planUnitCount.getCount();
      }
      total += planUnitCount.getCount();
    });

    // whatever fucking reason he decide to access totalCount from PlanResult.ts(107,34)
    this.totalCount = total
    // check is_passenger = true , passengerTotal is full or not  
    this.isAllFull = passengerTotal >= this.passengerMax;
  }

  // getTotalCount() {
  //   return this.totalCount;
  // }

  updateCount() {
    this.updatePassengerCount();
    if (this.isMoreThanFull)
      this.$root.attr("count-state", "more-than-full");
    else
      this.$root.attr("count-state", this.isAllFull ? "full" : "normal");

    this.planUnitCountList.forEach((planUnitCount: PlanUnitCount) => {
      if (planUnitCount.data.is_passenger) {
        if (planUnitCount.data.unit_max)
          planUnitCount.setIsMaxLimited(planUnitCount.getCount() >= planUnitCount.data.unit_max && this.isAllFull);
        planUnitCount.setIsMaxLimited(this.isAllFull);
      } else if (planUnitCount.data.unit_max) {
        planUnitCount.setIsMaxLimited(planUnitCount.getCount() >= planUnitCount.data.unit_max);
      }
    });
    this.emitter.emit("count-change");
  }
}